// Presence layer: avatar roster (top-right toolbar) + cursor overlay
// (absolute layer over the matrix). Renders from `local.presence` /
// `local.me`. Avatars update on every state notify; cursors keep their
// DOM nodes around and just animate transform, so re-renders don't
// retrigger CSS transitions.

import type { ClientInfoPublic } from "../generated/ClientInfoPublic";
import { local, sendIdentify } from "../state";

const cursorNodes = new Map<string, HTMLElement>();
let cursorLayer: HTMLElement | null = null;
let matrixEl: HTMLElement | null = null;

export function renderRoster(): HTMLElement {
  const roster = document.createElement("div");
  roster.className = "roster";
  for (const c of [...local.presence.values()]) {
    roster.append(renderChip(c));
  }
  return roster;
}

function renderChip(c: ClientInfoPublic): HTMLElement {
  const chip = document.createElement("button");
  chip.type = "button";
  chip.className = "chip";
  chip.style.setProperty("--chip-color", c.color);
  chip.title = c.name + (local.me?.clientId === c.clientId ? " (you)" : "");
  chip.textContent = initialFor(c.name);
  if (local.me?.clientId === c.clientId) {
    chip.classList.add("chip--self");
    chip.addEventListener("click", () => promptRename());
  }
  return chip;
}

const MAX_NAME_LEN = 40;

function promptRename(): void {
  if (!local.me) return;
  openRenameModal(local.me.name);
}

function openRenameModal(currentName: string): void {
  closeRenameModal();

  const overlay = document.createElement("div");
  overlay.className = "modal__overlay";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeRenameModal();
  });

  const dialog = document.createElement("div");
  dialog.className = "modal";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");

  const title = document.createElement("h2");
  title.className = "modal__title";
  title.textContent = "Display name";

  const form = document.createElement("form");
  form.className = "modal__form";

  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = MAX_NAME_LEN;
  input.className = "modal__label-input";
  input.value = currentName;
  input.placeholder = "Anonymous";

  const row = document.createElement("label");
  row.className = "modal__row";
  const rowSpan = document.createElement("span");
  rowSpan.textContent = "Name";
  row.append(rowSpan, input);

  const error = document.createElement("p");
  error.className = "modal__error";
  error.hidden = true;

  const actions = document.createElement("div");
  actions.className = "modal__actions";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => closeRenameModal());

  const save = document.createElement("button");
  save.type = "submit";
  save.className = "btn btn--primary";
  save.textContent = "Save";

  actions.append(cancel, save);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const trimmed = input.value.trim();
    if (!trimmed) {
      error.textContent = "Name cannot be empty.";
      error.hidden = false;
      input.focus();
      return;
    }
    if (trimmed.length > MAX_NAME_LEN) {
      error.textContent = `Name must be ≤ ${MAX_NAME_LEN} characters.`;
      error.hidden = false;
      input.focus();
      return;
    }
    if (trimmed !== currentName) sendIdentify(trimmed);
    closeRenameModal();
  });

  form.append(row, error, actions);
  dialog.append(title, form);
  overlay.append(dialog);
  document.body.append(overlay);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeRenameModal();
      window.removeEventListener("keydown", onKey);
    }
  };
  window.addEventListener("keydown", onKey);

  input.focus();
  input.select();
}

function closeRenameModal(): void {
  for (const ov of document.querySelectorAll(".modal__overlay")) ov.remove();
}

function initialFor(name: string): string {
  const first = name.trim()[0];
  return (first ?? "?").toUpperCase();
}

/** Attach the cursor overlay to the matrix container. Called once per
 *  board mount; subsequent state updates keep using the same layer. */
export function mountCursorLayer(matrix: HTMLElement): void {
  matrixEl = matrix;
  cursorLayer = document.createElement("div");
  cursorLayer.className = "cursor-layer";
  cursorLayer.setAttribute("aria-hidden", "true");
  matrix.append(cursorLayer);
  cursorNodes.clear();
  updateCursors();
}

export function unmountCursorLayer(): void {
  cursorLayer = null;
  matrixEl = null;
  cursorNodes.clear();
}

/** Reposition all remote cursors. Each remote cursor reports a quadrant
 *  + intra-quadrant (x, y) in [0, 1]; we project into the receiver's own
 *  rect for that quadrant so the cursor lands at the same content-
 *  relative spot regardless of viewport size. Idempotent. */
export function updateCursors(): void {
  if (!cursorLayer || !matrixEl) return;
  const matrixRect = matrixEl.getBoundingClientRect();
  const meId = local.me?.clientId;
  const present = new Set<string>();
  for (const c of local.presence.values()) {
    if (c.clientId === meId) continue;
    if (!c.cursor) continue;
    const qEl = matrixEl.querySelector<HTMLElement>(
      `[data-quadrant="${c.cursor.quadrant}"]`,
    );
    if (!qEl) continue;
    const qRect = qEl.getBoundingClientRect();
    if (qRect.width === 0 || qRect.height === 0) continue;
    present.add(c.clientId);
    let node = cursorNodes.get(c.clientId);
    if (!node) {
      node = createCursorNode(c);
      cursorLayer.append(node);
      cursorNodes.set(c.clientId, node);
    } else {
      node.style.setProperty("--cursor-color", c.color);
      const label = node.querySelector(".cursor__label");
      if (label) label.textContent = c.name;
    }
    const px = qRect.left - matrixRect.left + c.cursor.x * qRect.width;
    const py = qRect.top - matrixRect.top + c.cursor.y * qRect.height;
    node.style.transform = `translate(${px}px, ${py}px)`;
  }
  // Remove cursors for clients who left or stopped moving.
  for (const [id, node] of cursorNodes) {
    if (!present.has(id)) {
      node.remove();
      cursorNodes.delete(id);
    }
  }
}

function createCursorNode(c: ClientInfoPublic): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "cursor";
  wrap.style.setProperty("--cursor-color", c.color);
  wrap.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M0 0 L0 13 L4 9 L7 16 L9 15 L7 8 L13 8 Z" fill="currentColor"
            stroke="white" stroke-width="0.8" />
    </svg>
    <span class="cursor__label"></span>
  `;
  const label = wrap.querySelector(".cursor__label")!;
  label.textContent = c.name;
  return wrap;
}
